"use client";

import React from "react";
import { Sparkles, Ellipsis, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const carouselItems = [{
  title: "Industrial Foundations",
  subtitle: "Precision support for heavy machinery and structures",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_4355-1.webp?v=zl28rfghz9q",
  tags: ["High Strength", "Structure", "Industrial"]
}, {
  title: "Custom Landscape Masonry",
  subtitle: "Blending traditional stone craft with modern aesthetics",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621168/img/custom_landscape_masonry.png",
  tags: ["Stone Art", "Custom", "Aesthetics"]
}, {
  title: "Commercial Concrete Flooring",
  subtitle: "High-precision leveling and extreme durability",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621168/img/commercial_concrete_flooring.png",
  tags: ["Precision", "Flooring", "Commercial"]
}];
const gridItems = [{
  location: "Stamped Concrete",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/promotion-1024x768-1.jpg?v=gd07zg9mbhm",
  span: "md:col-span-8",
  height: "h-[400px] md:h-[500px]"
}, {
  location: "Basement Stairs",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/BASEMEN.jpg?v=4vuufw0pd7i",
  span: "md:col-span-4",
  height: "h-[400px] md:h-[500px]"
}, {
  location: "Concrete Foundation",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/IMG_5455-1.png?v=iastb6c0d9m",
  span: "md:col-span-4",
  height: "h-[300px] md:h-[400px]"
}, {
  location: "Concrete Demolition",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/image_b78q9rau.png?v=dajizf8fyc7",
  span: "md:col-span-4",
  height: "h-[300px] md:h-[400px]"
}, {
  location: "Block Wall",
  image: "https://sampsonconcreteandmasonry.net/wp-content/uploads/2026/04/Untitled-design.png?v=7fa9ua20xpn",
  span: "md:col-span-4",
  height: "h-[300px] md:h-[400px]"
}];
export default function PhotoAlbum() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: carouselRef,
    isVisible: isCarouselVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="167540" id="photoslide" className="bg-background text-foreground relative py-[160px] overflow-hidden bg-gradient-to-b from-background via-muted/30 to-background">
      <div className="container mx-auto px-4 md:px-6">
        
        <div ref={headerRef} className={`max-w-4xl mx-auto text-center mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="tracking-tighter mb-8 font-default font-black text-3xl md:text-4xl text-primary uppercase tracking-widest">Sampson Concrete & Masonry is your best choice. Serving the Communities of Brantford, Hamilton, Brant, Cambridge, and near by Cities</h2>
          <p className="text-muted-foreground leading-loose font-sans font-light text-lg md:text-xl max-w-3xl mx-auto">Up to 5 years warranty, Sampson Concrete and Masonry offers professional, reliable, and high-quality concrete and masonry services with over 15 years of hands-on experience. Our team is passionate about delivering exceptional results on every project, from concrete driveways, sidewalks, and stairs to basement, basement walkout, concrete patios, crack repairs, brick, stone, and block work—even custom jobs. We pride ourselves on completing projects efficiently without compromising quality, all at competitive pricing. Available 24/7 by phone, Sampson Concrete and Masonry serves Brant, Brantford, Hamilton, Oxford, Cambridge, and surrounding areas. Whatever your concrete or masonry needs, we’re ready to bring your project to life—just ask, and we’ll make it happen.</p>
        </div>

        <div ref={carouselRef} className={`mb-24 transition-all duration-1000 delay-200 ease-out ${isCarouselVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Carousel opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent>
              {carouselItems.map((item, i) => <CarouselItem key={i} data-index={i} className="md:basis-4/5 lg:basis-3/4 pl-4 md:pl-6">
                  <div className="relative group overflow-hidden rounded-lg aspect-video md:aspect-[21/9] ring-1 ring-white/10 shadow-xl">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 saturate-100 group-hover:saturate-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-80 transition-opacity group-hover:opacity-90" />
                    
                    <div className="absolute bottom-0 left-0 p-6 md:p-10 w-full flex flex-col md:flex-row md:items-end justify-between gap-4">
                      <div className="space-y-2">
                        <h3 className="text-white text-2xl md:text-3xl font-bold tracking-tight">{item.title}</h3>
                        <p className="text-white/70 text-sm md:text-base font-light">{item.subtitle}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 transition-all duration-1000 delay-400 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {gridItems.map((item, i) => <div key={i} data-index={i} className={`${item.span} ${item.height} relative group overflow-hidden rounded-lg bg-muted shadow-lg border border-border/50`}>
              <img src={item.image} alt={item.location} className="w-full h-full object-cover transition-all duration-700 ease-in-out group-hover:scale-110 saturate-100 group-hover:saturate-125" />
              <div className="absolute inset-0 bg-black/40 transition-colors duration-500 group-hover:bg-black/10" />
              
              <Badge variant="secondary" className="absolute top-4 left-4 md:top-6 md:left-6 bg-accent/90 backdrop-blur-md border-none px-4 py-2 shadow-xl transition-all duration-300 group-hover:-translate-y-1 rounded-md font-default font-bold text-accent-foreground hover:bg-accent">
                <MapPin className="mr-2 size-4 text-accent-foreground" />
                {item.location}
              </Badge>
            </div>)}
        </div>

      </div>

      <div className="fixed bottom-6 right-6 md:bottom-10 md:right-10 z-50 flex items-center gap-2 rounded-lg bg-primary p-2 shadow-2xl transition-all hover:-translate-y-2 duration-300 ring-2 ring-accent/30">
        <Link to="#" className="size-12 rounded-md text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-colors inline-flex items-center justify-center">
          <Ellipsis className="size-6" />
        </Link>
      </div>
    </section>;
}